import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Trash2, Plus, Lock } from "lucide-react";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [rows, setRows] = useState([]);
  const [cidr, setCidr] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");

  const loadSession = async () => {
    try {
      const { data } = await api.get("/admin/session");
      setAuthed(data.authenticated);
      if (data.authenticated) loadList();
    } catch {
      setAuthed(false);
    }
  };

  const loadList = async () => {
    try {
      const { data } = await api.get("/admin/allowlist");
      setRows(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadSession();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/login", { password });
      setAuthed(true);
      loadList();
    } catch {
      setError("Invalid password.");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/allowlist", { cidr, label });
      setCidr("");
      setLabel("");
      loadList();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add entry.");
    }
  };

  const remove = async (c) => {
    await api.delete(`/admin/allowlist?cidr=${encodeURIComponent(c)}`);
    loadList();
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-asphalt">Allowlist manager</h1>
        <p className="mt-2 text-sm text-muted">
          Manage the IP and CIDR allowlist for the classification API. When the list is empty, access is open.
        </p>

        {!authed ? (
          <form onSubmit={login} className="mt-8 max-w-sm space-y-3 rounded-xl border border-line bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-asphalt">
              <Lock className="h-4 w-4" />
              <span className="text-sm font-medium">Admin sign in</span>
            </div>
            <input
              type="password"
              data-testid="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full rounded-brand border border-line px-3 py-2.5 text-sm text-asphalt outline-none focus:border-asphalt"
            />
            <button type="submit" data-testid="admin-login-btn" className="gl-primary-btn w-full py-2.5 text-sm">
              Sign in
            </button>
            {error && <p className="text-sm text-tier-prohibited">{error}</p>}
          </form>
        ) : (
          <div className="mt-8 space-y-6">
            <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-white p-6 shadow-sm">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted">IP or CIDR</label>
                <input
                  data-testid="admin-cidr"
                  value={cidr}
                  onChange={(e) => setCidr(e.target.value)}
                  placeholder="203.0.113.5 or 203.0.113.0/24"
                  className="w-full rounded-brand border border-line px-3 py-2.5 text-sm text-asphalt outline-none focus:border-asphalt"
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted">Label</label>
                <input
                  data-testid="admin-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="office VPN"
                  className="w-full rounded-brand border border-line px-3 py-2.5 text-sm text-asphalt outline-none focus:border-asphalt"
                />
              </div>
              <button type="submit" data-testid="admin-add-btn" className="gl-primary-btn flex items-center gap-2 px-4 py-2.5 text-sm">
                <Plus className="h-4 w-4" /> Add
              </button>
            </form>
            {error && <p className="text-sm text-tier-prohibited">{error}</p>}

            <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-tint text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-3">IP / CIDR</th>
                    <th className="px-4 py-3">Label</th>
                    <th className="px-4 py-3">Added</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody data-testid="allowlist-table">
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-muted">
                        No entries. Access is currently open.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.cidr} className="border-t border-line">
                      <td className="px-4 py-3 font-medium text-asphalt">{r.cidr}</td>
                      <td className="px-4 py-3 text-muted">{r.label}</td>
                      <td className="px-4 py-3 text-muted">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => remove(r.cidr)}
                          className="text-tier-prohibited hover:opacity-70"
                          aria-label="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
